import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import { Modal, ModalBody, ModalFooter, Button, Input } from "reactstrap";
import i18n from "../../../i18n";
import { getServiceRequestAdmin, patchUpdateServiceNote } from "../../../helpers/api_rombo_helper";
import AlertSuccess from "../../../components/Alerts/AlertSuccess";

const ReferenceServiceModal = ({ isOpen, toggle, selectedService, onSuccess }) => {
  const [note, setNote] = useState("");
  const [isNoteValid, setIsNoteValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingNotes, setIsFetchingNotes] = useState(false);
  const [error, setError] = useState(null);
  const [showAlertSuccess, setShowAlertSuccess] = useState(false);
  const [alertTextHeader, setAlertTextHeader] = useState("");
  const [alertTextBody, setAlertTextBody] = useState("");

  const LoginProperties = createSelector(
    (state) => state.Login,
    (Login) => ({
      token: Login.token,
      loading: Login.loading,
      data: Login.data,
    })
  );

  const { token } = useSelector(LoginProperties);

  const getCleanServiceCode = (code) => {
    return code?.split("-").slice(0, 2).join("-");
  };

  useEffect(() => {
    if (isOpen && selectedService?.code) {
      const fetchServiceNotes = async () => {
        setIsFetchingNotes(true);
        setError(null);
        try {
          const serviceCode = getCleanServiceCode(selectedService.code);
          const serviceData = await getServiceRequestAdmin(token, serviceCode, {});          
          const existingNote = serviceData.info?.notes?.[0]?.message || "";
          setNote(existingNote);
          setIsNoteValid(existingNote.trim() !== "");
        } catch (error) {
          console.error("Error fetching service notes:", error);
          setNote("");
          setError(i18n.t("ErrorLoadingNotes"));
        } finally {
          setIsFetchingNotes(false);
        }
      };
      
      fetchServiceNotes();
    }
  }, [isOpen, selectedService, token]);

  const handleUpdateNote = async () => {
    if (!note.trim()) return;
  
    setIsLoading(true);
    setError(null);
  
    try {
      await patchUpdateServiceNote(token, selectedService.code, { message: note });
      
      toggle();
      
      setAlertTextHeader(i18n.t("NoteUpdatedSuccessfully"));
      setAlertTextBody(i18n.t("TheServiceNoteHasBeenUpdated"));
      setShowAlertSuccess(true);
      
      if (onSuccess) onSuccess();
      
    } catch (error) {
      console.error("Error updating service note:", error);
      setError(i18n.t("ErrorUpdatingNote"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        role="dialog"
        autoFocus={false}
        centered
        className="RequestModal"
        style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
        toggle={toggle}
        backdrop="static"
        size="lg"
      >
        <div>
          <div className="modal-header-container">
            <p className="cancellation-title modal-header-bottom">
              {i18n.t("EditReferenceNoteTitle")}
            </p>
            <p className="cancellation-text modal-service-id">
              {selectedService?.code}
            </p>
          </div>
          <div className="modal-separator" />
          <ModalBody>
            <p className="modal-text-bold">
              {i18n.t("EditReferenceNoteText")}
            </p>
            {error && (
              <div className="alert alert-danger">
                {error}
              </div>
            )}
            {isFetchingNotes ? (
              <div className="text-center py-3">
                <i className="bx bx-loader bx-spin font-size-24 align-middle me-2"></i>
                {i18n.t("LoadingNotes")}
              </div>
            ) : (
              <>
                <Input
                  type="textarea"
                  className="form-control"
                  placeholder={i18n.t("WriteYourNoteHere")}
                  value={note}
                  maxLength={200}
                  onChange={(e) => {
                    setNote(e.target.value.slice(0, 200));
                    setIsNoteValid(e.target.value.trim() !== "");
                  }}
                />
                <small className="text-muted">
                  {note.length}/200 {i18n.t("Characters")}
                </small>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              className="btn btn-users border"
              type="button"
              onClick={handleUpdateNote}
              disabled={!isNoteValid || isLoading || isFetchingNotes}
            >
              {isLoading ? i18n.t("Saving") : i18n.t("EditNote")}
            </Button>
            <Button className="btn btn-light" type="button" onClick={toggle}>
              {i18n.t("Cancel")}
            </Button>
          </ModalFooter>
        </div>
      </Modal>
      <AlertSuccess
        showAlertSuccess={showAlertSuccess}
        setShowAlertSuccess={setShowAlertSuccess}
        textHeader={alertTextHeader}
        textBody={alertTextBody}
      />
    </>
  );
};

ReferenceServiceModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  selectedService: PropTypes.object.isRequired,
  onSuccess: PropTypes.func,
};

export default ReferenceServiceModal;